import { useMemo } from "react";

/**
 * Tabla de contenidos navegable del guion.
 *
 * Se muestra a la izquierda del editor. Pinta una lista de escenas con su
 * número + heading truncado, marca la escena actual (en la que está el cursor)
 * y al hacer click hace scroll a esa escena. Encima/debajo, botones para ir
 * al principio / al final.
 *
 * Colapsable: en estado encogido se queda como una barra de 36 px con un
 * indicador discreto y un botón para volver a abrirlo.
 *
 * Atajos de teclado (gestionados por el padre):
 *   ⌘↑ / ⌘↓  Inicio / Final
 *   ⌘[ / ⌘]  Escena anterior / siguiente
 */
export default function NavigatorPanel({
  scenes,
  activeSceneNumber,
  totalWords,
  collapsed,
  onToggleCollapsed,
  onJumpToScene,
  onJumpToStart,
  onJumpToEnd,
}) {
  // Páginas estimadas: estándar Hollywood es ~1 página ≈ 1 minuto ≈ 250 palabras.
  const pages = useMemo(() => Math.max(1, Math.round((totalWords || 0) / 250)), [totalWords]);

  if (collapsed) {
    return (
      <aside
        data-testid="sp-navigator"
        className="hidden shrink-0 flex-col items-center gap-2 rounded-xl border border-stone-300/70 bg-white py-2 shadow-sm lg:flex lg:w-9"
      >
        <button
          onClick={onToggleCollapsed}
          data-testid="sp-nav-expand"
          title="Mostrar navegación"
          className="rounded-md p-1 text-stone-500 transition hover:bg-stone-100 hover:text-[#26775E]"
        >
          ››
        </button>
        <span className="rotate-180 text-[10px] uppercase tracking-widest text-stone-400" style={{ writingMode: "vertical-rl" }}>
          Navegar
        </span>
      </aside>
    );
  }

  return (
    <aside
      data-testid="sp-navigator"
      className="hidden shrink-0 flex-col overflow-hidden rounded-xl border border-stone-300/70 bg-white shadow-sm lg:flex lg:w-[240px] lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-6rem)]"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-stone-200 bg-[#FAFAF6] px-3 py-2">
        <span className="text-[10px] uppercase tracking-widest text-stone-500">Navegar</span>
        <button
          onClick={onToggleCollapsed}
          data-testid="sp-nav-collapse"
          title="Ocultar navegación"
          className="text-stone-400 transition hover:text-stone-700"
        >
          ‹‹
        </button>
      </div>

      {/* Top quick actions */}
      <div className="border-b border-stone-200 px-2 py-2">
        <button
          onClick={onJumpToStart}
          data-testid="sp-nav-start"
          className="flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs text-stone-700 transition hover:bg-stone-100 hover:text-[#26775E]"
        >
          <span>↑ Ir al inicio</span>
          <kbd className="font-mono text-[10px] text-stone-400">⌘↑</kbd>
        </button>
      </div>

      {/* Scenes list (scrollable) */}
      <ul className="flex-1 overflow-y-auto px-2 py-2 text-xs" data-testid="sp-nav-scenes">
        {scenes.length === 0 && (
          <li className="px-2 py-3 text-center text-stone-400">Sin escenas aún.</li>
        )}
        {scenes.map((s) => {
          const isActive = s.number === activeSceneNumber;
          return (
            <li key={s.elementId}>
              <button
                onClick={() => onJumpToScene(s)}
                data-testid={`sp-nav-scene-${s.number}`}
                title={s.heading}
                className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition ${
                  isActive
                    ? "bg-[#26775E]/10 text-[#26775E]"
                    : "text-stone-700 hover:bg-stone-100 hover:text-[#26775E]"
                }`}
              >
                <span className={`mt-0.5 w-3 shrink-0 text-center font-mono text-[10px] ${isActive ? "text-[#26775E]" : "text-stone-400"}`}>
                  {isActive ? "▸" : ""}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-stone-500">{String(s.number).padStart(2, "0")}</span>
                <span className="flex-1 truncate text-[11px] leading-snug">
                  {s.heading || <em className="text-stone-400">(escena sin título)</em>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Bottom: jump to end + page counter */}
      <div className="border-t border-stone-200 px-2 py-2">
        <button
          onClick={onJumpToEnd}
          data-testid="sp-nav-end"
          className="mb-1 flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs text-stone-700 transition hover:bg-stone-100 hover:text-[#26775E]"
        >
          <span>↓ Ir al final</span>
          <kbd className="font-mono text-[10px] text-stone-400">⌘↓</kbd>
        </button>
        <div className="flex items-center justify-between px-2 pt-1 text-[10px] uppercase tracking-widest text-stone-400">
          <span>Páginas ~ {pages}</span>
          <span>{totalWords || 0} palabras</span>
        </div>
        <div className="px-2 pt-2 text-[10px] leading-tight text-stone-400">
          <kbd className="font-mono text-stone-500">⌘[</kbd> / <kbd className="font-mono text-stone-500">⌘]</kbd> · escena anterior/siguiente
        </div>
      </div>
    </aside>
  );
}
